'use client';

import { useRegionLocale } from 'contexts/RegionLocaleContext';
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  fetchCampaigns,
  getActiveCampaigns,
  isCampaignsCountryCode,
  normalizeSddDeliveryBanner,
} from 'content/campaigns/api';
import { SDD_NDD_BADGE_ENABLED } from 'utils/featureFlags';
import { Campaign, SddDeliveryBanner } from 'content/campaigns/types';

interface UseCampaignsResult {
  campaigns: Campaign[];
  delivery: SddDeliveryBanner | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
  /** Refresh delivery copy without toggling `loading` (IST window crossing). */
  refetchQuiet: () => void;
}

export function useCampaigns(
  initialCampaigns?: Campaign[],
  initialSddDelivery?: SddDeliveryBanner | null,
): UseCampaignsResult {
  const [campaigns, setCampaigns] = useState<Campaign[]>(
    initialCampaigns ?? [],
  );
  const [delivery, setDelivery] = useState<SddDeliveryBanner | null>(
    SDD_NDD_BADGE_ENABLED ? (initialSddDelivery ?? null) : null,
  );
  const [loading, setLoading] = useState<boolean>(!initialCampaigns);
  const [error, setError] = useState<Error | null>(null);
  // Same source as the layout preload. Region may be GLOBAL; the campaigns
  // API only accepts ISO-2, which isCampaignsCountryCode enforces.
  const region = useRegionLocale().region;

  // Quiet and loud fetches share this counter so a slow older response
  // (e.g. sent just before an IST window crossing) never overwrites a
  // newer one — whichever request was issued last wins.
  const requestIdRef = useRef(0);

  const fetchData = useCallback(
    async (quiet = false) => {
      const requestId = ++requestIdRef.current;
      if (!isCampaignsCountryCode(region)) {
        if (!quiet) {
          setCampaigns([]);
          setDelivery(null);
          setError(null);
          setLoading(false);
        }
        return;
      }

      try {
        if (!quiet) {
          setLoading(true);
          setError(null);
        }
        const response = await fetchCampaigns(region);
        if (requestId !== requestIdRef.current) return;
        setCampaigns(getActiveCampaigns(response.campaigns ?? []));
        setDelivery(
          SDD_NDD_BADGE_ENABLED
            ? normalizeSddDeliveryBanner(response.delivery)
            : null,
        );
      } catch (err) {
        if (quiet || requestId !== requestIdRef.current) return;
        setError(
          err instanceof Error ? err : new Error('Failed to fetch campaigns'),
        );
        setCampaigns([]);
        setDelivery(null);
      } finally {
        // Unguarded: a superseding quiet fetch never manages `loading`, so a
        // superseded loud request must still clear its own spinner.
        if (!quiet) setLoading(false);
      }
    },
    [region],
  );

  const refetch = useCallback(() => {
    void fetchData(false);
  }, [fetchData]);

  const refetchQuiet = useCallback(() => {
    void fetchData(true);
  }, [fetchData]);

  useEffect(() => {
    if (initialCampaigns) {
      setCampaigns(initialCampaigns);
      setDelivery(SDD_NDD_BADGE_ENABLED ? (initialSddDelivery ?? null) : null);
      setLoading(false);
      return;
    }

    fetchData(false);
  }, [fetchData, initialCampaigns, initialSddDelivery]);

  return {
    campaigns,
    delivery,
    loading,
    error,
    refetch,
    refetchQuiet,
  };
}

export { getCampaignByProduct, getCampaignConfig } from 'content/campaigns/lookup';

